import React, { useState, useEffect } from "react";
import useApiGet from "../hooks/getData";
import useApiPost from "../hooks/postData";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import Searchbar from "../../components/Search";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";

// Gifts are first-come-first-served now, so chance_percent is unused by the app;
// the API still validates it (0-100), so it is always sent as 0.
const emptyItem = { coins: "" };

const emptyForm = {
  id: null,
  title: "",
  coins_required: "",
  level_number: "",
  is_active: true,
  sort_order: "",
  items: [{ ...emptyItem }],
};

function TreasureBoxList() {
  const { getData } = useApiGet();
  const { postData, loading: saving } = useApiPost();
  const isSidebarOpen = useSelector((state) => state.sidebar.isOpen);

  const [boxes, setBoxes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [deleteId, setDeleteId] = useState(null);

  const fetchBoxes = async () => {
    setLoading(true);
    try {
      const res = await getData("/treasure-box/admin-list");
      setBoxes(res?.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load treasure boxes.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBoxes();
    // eslint-disable-next-line
  }, []);

  const resetForm = () => {
    setForm(emptyForm);
    setIsEdit(false);
  };

  const openAddForm = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditForm = (box) => {
    setForm({
      id: box.id,
      title: box.title || "",
      coins_required: box.coins_required ?? "",
      level_number: box.level_number || "",
      is_active: !!box.is_active,
      sort_order: box.sort_order || "",
      items: box.items && box.items.length > 0
        ? box.items.map((i) => ({ coins: i.coins }))
        : [{ ...emptyItem }],
    });
    setIsEdit(true);
    setModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleItemChange = (index, field, value) => {
    setForm((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, items };
    });
  };

  const addItemRow = () => {
    if (form.items.length >= 5) {
      toast.error("Maximum 5 gifts allowed per box.");
      return;
    }
    setForm((prev) => ({ ...prev, items: [...prev.items, { ...emptyItem }] }));
  };

  const removeItemRow = (index) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.title || form.coins_required === "") {
      toast.error("Please fill title and coins required.");
      return;
    }
    if (form.items.length === 0) {
      toast.error("Add at least one gift.");
      return;
    }
    for (const item of form.items) {
      if (!item.coins || Number(item.coins) < 1) {
        toast.error("Enter coins (minimum 1) for every gift.");
        return;
      }
    }

    const payload = {
      title: form.title,
      coins_required: Number(form.coins_required),
      level_number: form.level_number !== "" ? Number(form.level_number) : 0,
      is_active: form.is_active ? 1 : 0,
      sort_order: Number(form.sort_order) || 0,
      items: form.items.map((i) => ({
        coins: Number(i.coins),
        chance_percent: 0,
      })),
    };

    try {
      const endpoint = isEdit ? "/treasure-box/edit" : "/treasure-box/create";
      const finalPayload = isEdit ? { id: form.id, ...payload } : payload;

      const res = await postData(endpoint, finalPayload);

      if (res?.success) {
        toast.success(res.message || "Saved successfully!");
        setModalOpen(false);
        resetForm();
        fetchBoxes();
      } else {
        toast.error(res?.message || "Something went wrong.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save.");
    }
  };

  const handleDelete = async () => {
    try {
      const res = await postData("/treasure-box/delete", { id: deleteId });
      if (res?.success) {
        toast.success(res.message || "Deleted!");
        setDeleteId(null);
        fetchBoxes();
      } else {
        toast.error(res?.message || "Failed to delete.");
      }
    } catch (err) {
      toast.error("Failed to delete.");
    }
  };

  return (
    <div className={`dark:bg-primary ${isSidebarOpen ? "xl:pl-20" : "xl:pl-72"}`}>
      <Searchbar />
      <div className="px-4 pb-10 xl:px-6">

        <div className="flex items-center justify-between py-3">
          <h2 className="text-[#000000] font-semibold font-poppins text-xl pt-3 dark:text-darkText">
            Treasure Boxes
          </h2>
          <button
            style={{ backgroundColor: "#0D9947" }}
            className="px-6 py-2.5 text-sm font-medium text-white rounded-lg font-poppins"
            onClick={openAddForm}
          >
            + Add Box
          </button>
        </div>

        {loading ? (
          <p className="font-poppins dark:text-darkText">Loading...</p>
        ) : boxes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32">
            <h2 className="text-sm font-semibold font-poppins text-[#000000] dark:text-darkText">
              Don't have any data to show
            </h2>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {boxes.map((box) => (
              <div
                key={box.id}
                className="p-4 border border-opacity-gradient dark:border-[#1F1F1F] rounded-xl bg-white dark:bg-primary"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-poppins font-semibold text-sm dark:text-darkText">
                      {box.title}
                    </p>
                    <p className="font-poppins text-xs text-[#593A99] pt-1">
                      Opens when a room shares {box.coins_required} coins
                    </p>
                    {box.level_number > 0 && (
                      <p className="font-poppins text-xs text-[#999999] pt-0.5">
                        Level {box.level_number}
                      </p>
                    )}
                  </div>
                  <span
                    className={`px-2 py-0.5 text-xs rounded-full font-poppins ${
                      box.is_active
                        ? "text-[#0D9947] bg-[#0D9947] bg-opacity-15"
                        : "text-[#999999] bg-[#999999] bg-opacity-15"
                    }`}
                  >
                    {box.is_active ? "Active" : "Inactive"}
                  </span>
                </div>

                <div className="pt-3 space-y-1">
                  <p className="text-[11px] font-poppins text-[#999999]">
                    {box.items.length} gift{box.items.length === 1 ? "" : "s"} (first come, first served)
                  </p>
                  {box.items.map((item) => (
                    <div
                      key={item.id}
                      className="text-xs font-poppins dark:text-darkText"
                    >
                      🪙 {item.coins} coins
                    </div>
                  ))}
                </div>

                <div className="flex gap-2 pt-4">
                  <button
                    onClick={() => openEditForm(box)}
                    className="flex-1 px-3 py-1.5 text-xs font-poppins text-[#593A99] border border-[#593A99] rounded-lg"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setDeleteId(box.id)}
                    className="flex-1 px-3 py-1.5 text-xs font-poppins text-[#D92D20] border border-[#D92D20] rounded-lg"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{isEdit ? "Edit Treasure Box" : "Add Treasure Box"}</DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-3 py-2">
            <div>
              <label className="text-sm font-poppins dark:text-darkText">Title</label>
              <input
                type="text"
                name="title"
                value={form.title}
                onChange={handleChange}
                className="w-full border border-opacity-gradient rounded-lg p-2 dark:bg-transparent dark:text-darkText"
                placeholder="e.g. Level 1 Box"
              />
            </div>

            <div>
              <label className="text-sm font-poppins dark:text-darkText">
                Coins Required (total coins shared by everyone in an audio room to open this box)
              </label>
              <input
                type="number"
                name="coins_required"
                value={form.coins_required}
                onChange={handleChange}
                className="w-full border border-opacity-gradient rounded-lg p-2 dark:bg-transparent dark:text-darkText"
                placeholder="e.g. 500"
              />
            </div>

            <div>
              <label className="text-sm font-poppins dark:text-darkText">
                Level Number (optional, for display only)
              </label>
              <input
                type="number"
                name="level_number"
                value={form.level_number}
                onChange={handleChange}
                className="w-full border border-opacity-gradient rounded-lg p-2 dark:bg-transparent dark:text-darkText"
                placeholder="e.g. 1 (optional)"
              />
            </div>

            <div>
              <label className="text-sm font-poppins dark:text-darkText">Sort Order</label>
              <input
                type="number"
                name="sort_order"
                value={form.sort_order}
                onChange={handleChange}
                className="w-full border border-opacity-gradient rounded-lg p-2 dark:bg-transparent dark:text-darkText"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                name="is_active"
                checked={form.is_active}
                onChange={handleChange}
                id="box_is_active"
              />
              <label htmlFor="box_is_active" className="text-sm font-poppins dark:text-darkText">
                Active
              </label>
            </div>

            <div className="pt-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-poppins font-semibold dark:text-darkText">
                  Gifts (max 5)
                </label>
              </div>

              {form.items.map((item, index) => (
                <div key={index} className="flex items-center gap-2 pt-2">
                  <input
                    type="number"
                    placeholder="Coins in this gift"
                    min="1"
                    value={item.coins}
                    onChange={(e) => handleItemChange(index, "coins", e.target.value)}
                    className="flex-1 border border-opacity-gradient rounded-lg p-2 dark:bg-transparent dark:text-darkText"
                  />
                  {form.items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItemRow(index)}
                      className="text-[#D92D20] text-xs font-poppins px-2"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}

              {form.items.length < 5 && (
                <button
                  type="button"
                  onClick={addItemRow}
                  className="mt-2 text-xs font-poppins text-[#593A99] underline"
                >
                  + Add another gift
                </button>
              )}

              <p className="text-[11px] text-[#999999] font-poppins pt-2">
                When the box opens in a room, the first user to tap a gift wins it (one gift per user per box).
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-3">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="px-6 py-2.5 text-sm font-medium border border-header rounded-lg font-poppins dark:text-darkText"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                style={{ backgroundColor: "#593A99" }}
                className="px-6 py-2.5 text-sm font-medium text-white rounded-lg font-poppins"
              >
                {saving ? "Saving..." : isEdit ? "Update" : "Add"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Modal */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete Treasure Box?</DialogTitle>
        <DialogContent>
          <p className="pb-4 font-poppins dark:text-darkText">
            This action cannot be undone. Are you sure?
          </p>
          <div className="flex justify-end gap-2 pb-2">
            <button
              onClick={() => setDeleteId(null)}
              className="px-6 py-2.5 text-sm font-medium border border-header rounded-lg font-poppins dark:text-darkText"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              style={{ backgroundColor: "#D92D20" }}
              className="px-6 py-2.5 text-sm font-medium text-white rounded-lg font-poppins"
            >
              Delete
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default TreasureBoxList;